import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, Zap, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { usePartner } from "@/context/PartnerContext";
import { DiscountCodeBox } from "@/components/DiscountCodeBox";
import { gbp, nextBoxUpgrade, formatWeight, boostLabel, mixDiscountPct, offerQtyInCart, bottleOffer, nextOfferStep, guestBundlePct, mixboxRetailPrice, mixboxBoxPrice, isTradeMode, guestTierPrice } from "@/lib/pricing";

const bshort = (c) => (c === "x2" ? "×2" : c === "x3" ? "×3" : "");
const mixboxLineTotal = (it) => ((it.type === "mixbox" || it.type === "economy") ? (it.box_price ?? it.unit_price * it.bottle_count) : it.unit_price) * it.quantity;

export default function Cart() {
  const { items, updateQty, updateItem, removeItem, subtotal, shipCountry, setShipCountry, discountCode } = useCart();
  const { ref: partnerRef } = usePartner();
  const nav = useNavigate();
  const [zones, setZones] = useState([]);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [premLadders, setPremLadders] = useState({});
  const country = shipCountry || "United Kingdom";

  useEffect(() => {
    api.get("/flavors").then((r) => {
      const m = {};
      (r.data || []).forEach((f) => {
        if (f.range_type === "guest") m[f.id] = { retail: f.guest_retail_tiers || [], trade: f.guest_trade_tiers || [], base: Number(f.guest_price || 0) };
      });
      setPremLadders(m);
    }).catch(() => {});
  }, []);

  // "Add N more Premium to unlock the next price tier" — tier is by total Premium bottles in basket.
  const premiumNudge = useMemo(() => {
    const guestItems = items.filter((i) => i.type === "guest");
    if (!guestItems.length) return null;
    const totalQty = guestItems.reduce((s, i) => s + i.quantity, 0);
    const trade = isTradeMode();
    const ladderFor = (it) => {
      const L = premLadders[it.flavor_id];
      if (!L) return null;
      return { tiers: (trade && (L.trade || []).length) ? L.trade : (L.retail || []), base: L.base };
    };
    const breaks = new Set();
    guestItems.forEach((it) => {
      const L = ladderFor(it);
      (L?.tiers || []).forEach((t) => { if (Number(t.min_qty) > totalQty) breaks.add(Number(t.min_qty)); });
    });
    const sorted = [...breaks].sort((a, b) => a - b);
    for (const nextBreak of sorted) {
      let saving = 0;
      guestItems.forEach((it) => {
        const L = ladderFor(it);
        if (!L) return;
        const cur = guestTierPrice(L.tiers, totalQty, L.base);
        const nxt = guestTierPrice(L.tiers, nextBreak, L.base);
        saving += Math.max(0, cur - nxt) * it.quantity;
      });
      if (saving > 0) return { need: nextBreak - totalQty, nextBreak, saving: Math.round(saving * 100) / 100, key: guestItems[0].key, qty: guestItems[0].quantity };
    }
    return null;
  }, [items, premLadders]);

  // Tier-aware per-bottle price for Premium (guest) lines so the visible Subtotal
  // matches the authoritative /checkout/quote total (which re-tiers by total qty).
  const totalPremiumQty = useMemo(() => items.filter((i) => i.type === "guest").reduce((s, i) => s + i.quantity, 0), [items]);
  const guestUnit = (it) => {
    const L = premLadders[it.flavor_id];
    if (!L) return it.unit_price;
    const tiers = (isTradeMode() && (L.trade || []).length) ? L.trade : (isTradeMode() ? [] : (L.retail || []));
    if (!tiers.length) return it.unit_price; // legacy trade discount/override handled at add + quote
    const optsTotal = (it.options || []).reduce((s, o) => s + (Number(o.price) || 0), 0);
    return Math.round((guestTierPrice(tiers, totalPremiumQty, L.base) + optsTotal) * 100) / 100;
  };
  const lineTotalOf = (it) => (it.type === "guest" ? guestUnit(it) * it.quantity : mixboxLineTotal(it));
  const displaySubtotal = useMemo(() => Math.round(items.reduce((a, it) => a + lineTotalOf(it), 0) * 100) / 100, [items, premLadders, totalPremiumQty]);

  const mixpackSavings = Math.round(items.reduce((a, it) => {
    const d = it.mixpack_discount || 0;
    return it.type === "mixbox" && d > 0 ? a + mixboxLineTotal(it) * (d / (1 - d)) : a;
  }, 0) * 100) / 100;

  const isWholesale = items.some((i) => i.is_wholesale);
  const retailSavings = isTradeMode() ? Math.round(items.reduce((a, it) => {
    if (it.type !== "mixbox") return a;
    const r = mixboxRetailPrice(it);
    return r == null ? a : a + Math.max(0, (r - mixboxBoxPrice(it)) * it.quantity);
  }, 0) * 100) / 100 : 0;
  const sortedZones = useMemo(() => zones.map((z) => ({ ...z, countries: [...(z.countries || [])].sort() })), [zones]);

  useEffect(() => {
    api.get("/shipping/zones").then((r) => setZones(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length === 0) { setQuote(null); return; }
    const payload = {
      items: items.map((it) => {
        if (it.type === "single") return { type: "single", flavor_id: it.flavor_id, flavor_name: it.flavor_name, base: it.base, size_id: it.size_id, quantity: it.quantity };
        if (it.type === "mixbox") return { type: "mixbox", size_id: it.size_id, selections: it.selections, quantity: it.quantity, mixpack_discount: it.mixpack_discount || 0 };
        if (it.type === "economy") return { type: "economy", size_id: it.size_id, selections: it.selections.map((s) => ({ flavor_id: s.flavor_id, boost: s.boost, quantity: s.quantity })), quantity: it.quantity };
        if (it.type === "guest") return { type: "guest", flavor_id: it.flavor_id, quantity: it.quantity, options: (it.options || []).map((o) => o.label) };
        if (it.type === "offer") return { type: "offer", flavor_id: it.flavor_id, base: it.base, size_id: it.size_id, quantity: it.quantity };
        return { type: "multipack", multipack_id: it.multipack_id, selections: it.selections, quantity: it.quantity };
      }),
      customer_name: "x", email: "quote@dreamlabs.uk", address: "x", city: "x", postcode: "x",
      country, currency: "BTC", is_wholesale: isWholesale, discount_code: discountCode || null, ref: partnerRef || null,
    };
    api.post("/checkout/quote", payload)
      .then((r) => { setQuote(r.data); setQuoteError(""); })
      .catch((e) => { setQuote(null); setQuoteError(apiError(e)); });
    // eslint-disable-next-line
  }, [items, country, discountCode]);

  const applyUpgrade = (it, up) => {
    updateItem(it.key, { selections: up.selections, box_price: up.box_price });
    toast.success(`Box upgraded to ${boostLabel(up.boost)}`);
  };
  const offerFull = offerQtyInCart(items) >= (Number(bottleOffer().max_qty) || 1);
  const bundlePct = guestBundlePct();
  const bundleSaving = quote?.discount?.guest_bundle_saving || 0;

  if (items.length === 0) {
    return (
      <div className="mx-auto flex max-w-[1400px] flex-col items-center px-6 py-28 text-center">
        <ShoppingBag className="h-10 w-10 text-muted-foreground" />
        <h1 className="mt-6 font-heading text-3xl font-bold">Your cart is empty</h1>
        <Link to="/shop" className="mt-6 bg-foreground px-6 py-3 text-sm font-medium text-background">Browse Flavors</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-14">
      <h1 className="font-heading text-5xl font-black tracking-tighter">Cart</h1>
      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 border border-border" data-testid="cart-items">
          {items.map((it) => {
            const up = nextBoxUpgrade(it);
            const showUpsell = !!up;
            return (
            <div key={it.key} className="border-b border-border p-5 last:border-b-0" data-testid={`cart-item-${it.key}`}>
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 shrink-0 rounded-full" style={{ background: it.type === "single" ? it.accent_color : "#0A0A0A" }} />
                <div className="flex-1">
                  <div className="font-heading text-lg font-semibold">
                    {it.type === "single" || it.type === "offer" ? `${it.flavor_name}` : it.type === "mixbox" ? `${it.bottle_count} × ${it.size_label} Box` : it.type === "economy" ? `${it.bottle_count} × ${it.size_label} Economy Box` : it.type === "guest" ? it.flavor_name : it.name}
                    {it.type === "single" && it.boost && it.boost !== "standard" && <span className="ml-2 text-sm text-primary">{it.boost_label}</span>}
                    {it.type === "offer" && <span className="mono ml-2 border border-primary px-1.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-primary" data-testid={`cart-offer-badge-${it.key}`}>Offer</span>}
                  </div>
                  <div className="mono text-xs text-muted-foreground">
                    {it.type === "single" || it.type === "offer"
                      ? `${it.size_label} · Base ${it.base}${it.is_wholesale ? " · Wholesale" : ""}${it.type === "offer" ? " · Checkout offer" : ""}`
                      : it.type === "guest"
                      ? `${it.brand_name ? it.brand_name + " · " : ""}${it.size_label || "Guest"}${(it.options && it.options.length) ? " · " + it.options.map((o) => o.label).join(", ") : ""}`
                      : it.type === "economy"
                      ? it.selections.map((s) => `${s.quantity}× ${s.flavor_name}${s.boost && s.boost !== "standard" ? ` (${bshort(s.boost)})` : ""}`).join(", ")
                      : it.selections.map((s) => `${s.quantity}× ${s.flavor_name}${s.base ? ` (${s.base}${s.boost && s.boost !== "standard" ? ` ${bshort(s.boost)}` : ""})` : ""}`).join(", ")}
                  </div>
                  <div className="mono mt-1 text-xs">{it.type === "mixbox" ? `${gbp(it.base_unit_price ?? it.unit_price)} base / bottle` : it.type === "economy" ? "Economy box" : `${gbp(it.type === "guest" ? guestUnit(it) : it.unit_price)} each`}</div>
                </div>
                {(it.type === "mixbox" || it.type === "economy") ? (
                  <div className="mono text-xs text-muted-foreground" data-testid={`cart-box-count-${it.key}`}>{it.bottle_count} bottles</div>
                ) : (
                  <div className="flex items-center border border-border">
                    <button data-testid={`cart-minus-${it.key}`} onClick={() => updateQty(it.key, it.quantity - 1)} className="p-2 hover:bg-muted"><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-10 text-center mono text-sm">{it.quantity}</span>
                    <button data-testid={`cart-plus-${it.key}`} disabled={it.type === "offer" && offerFull} onClick={() => updateQty(it.key, it.quantity + 1)} className="p-2 hover:bg-muted disabled:opacity-30"><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                )}
                <div className="w-20 text-right mono font-bold">{gbp(lineTotalOf(it))}</div>
                <button data-testid={`cart-remove-${it.key}`} onClick={() => removeItem(it.key)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
              {showUpsell && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border border-primary/40 bg-primary/10 px-3 py-2" data-testid={`cart-upsell-${it.key}`}>
                  <div className="flex items-center gap-2 text-xs">
                    <Zap className="h-4 w-4 shrink-0 text-primary" />
                    <span>Make every bottle <span className="font-bold text-primary">{boostLabel(up.boost)}</span> — <span className="font-bold">+{gbp(up.per_bottle)} per bottle</span> · +{gbp(up.delta)} for the box</span>
                  </div>
                  <button data-testid={`cart-upsell-btn-${it.key}`} onClick={() => applyUpgrade(it, up)}
                    className="shrink-0 border border-primary bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">
                    Upgrade to {up.short}
                  </button>
                </div>
              )}
            </div>
            );
          })}
          <div className="p-5 pt-0">
            {premiumNudge && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border border-primary/50 bg-primary/10 px-3 py-2.5" data-testid="cart-premium-tier-nudge">
                <div className="flex items-center gap-2 text-xs">
                  <Zap className="h-4 w-4 shrink-0 text-primary" />
                  <span>
                    Add <span className="font-bold text-primary">{premiumNudge.need} more Premium bottle{premiumNudge.need > 1 ? "s" : ""}</span> to reach the <span className="font-bold">{premiumNudge.nextBreak}+</span> tier — you'd save <span className="font-bold text-primary">{gbp(premiumNudge.saving)}</span> on the Premium bottles already in your basket.
                  </span>
                </div>
                <button data-testid="cart-premium-tier-add" onClick={() => updateQty(premiumNudge.key, premiumNudge.qty + premiumNudge.need)}
                  className="shrink-0 border border-primary bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">
                  Add {premiumNudge.need} more
                </button>
              </div>
            )}
            <Link to="/shop" data-testid="continue-shopping-btn" className="mt-4 inline-flex items-center gap-2 border border-border px-4 py-2.5 text-sm font-medium transition-colors hover:border-foreground">
              <ArrowLeft className="h-4 w-4" /> Continue shopping
            </Link>
          </div>
        </div>

        <div className="h-fit border border-border p-6">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Summary</div>
          <div className="mt-4 flex justify-between text-sm"><span>Subtotal</span><span className="mono font-bold" data-testid="cart-subtotal">{gbp(displaySubtotal)}</span></div>
          {bundleSaving > 0 && (
            <div className="mt-2 flex justify-between text-sm text-primary" data-testid="cart-bundle-savings">
              <span>Guest bundle saving ({quote?.discount?.guest_bundle_pct || bundlePct}%)</span><span className="mono font-semibold">−{gbp(bundleSaving)}</span>
            </div>
          )}
          {mixpackSavings > 0 && (
            <div className="mt-2 flex justify-between text-sm text-primary" data-testid="cart-mixpack-savings">
              <span>Mixed-size saving ({mixDiscountPct()}%)</span><span className="mono font-semibold">−{gbp(mixpackSavings)}</span>
            </div>
          )}
          {retailSavings > 0 && (
            <div className="mt-2 flex justify-between text-sm text-green-600" data-testid="cart-retail-savings">
              <span>Your trade price vs standard retail</span><span className="mono font-semibold">−{gbp(retailSavings)}</span>
            </div>
          )}
          {(quote?.discount?.volume_saving || 0) > 0 && (
            <div className="mt-2 flex justify-between text-sm text-primary" data-testid="cart-volume-savings">
              <span>Volume discount ({quote.discount.volume_pct}% off)</span><span className="mono font-semibold">−{gbp(quote.discount.volume_saving)}</span>
            </div>
          )}
          {(quote?.discount?.code_saving || 0) > 0 && (
            <div className="mt-2 flex justify-between text-sm text-primary" data-testid="cart-code-savings">
              <span>Code {quote.discount.code?.code} · {quote.discount.code?.label}</span><span className="mono font-semibold">−{gbp(quote.discount.code_saving)}</span>
            </div>
          )}
          <DiscountCodeBox quote={quote} testPrefix="cart" />
          <div className="mt-4 border-t border-border pt-4">
            <label className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Ship to</label>
            <select data-testid="cart-country" value={country} onChange={(e) => setShipCountry(e.target.value)}
              className="mt-1.5 block w-full rounded-none border border-border bg-background p-2.5 text-sm">
              {sortedZones.map((z) => (
                <optgroup key={z.name} label={z.name}>
                  {z.countries.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          {quoteError ? (
            <div className="mt-3 text-sm text-destructive" data-testid="cart-quote-error">{quoteError}</div>
          ) : (
            <>
              <div className="mt-3 flex justify-between text-sm text-muted-foreground">
                <span>Weight</span><span className="mono">{quote ? formatWeight(quote.total_weight_g) : "—"}</span>
              </div>
              <div className="mt-2 flex justify-between text-sm">
                <span>Shipping {quote?.shipping_meta?.zone_name ? <span className="text-muted-foreground">· {quote.shipping_meta.zone_name}</span> : ""}</span>
                <span className="mono font-semibold" data-testid="cart-shipping">{quote ? (quote.shipping === 0 ? "FREE" : gbp(quote.shipping)) : "—"}</span>
              </div>
              {quote?.shipping_meta?.free_shipping_applied && (
                <div className="mono mt-1 text-[11px] uppercase tracking-[0.1em] text-green-600" data-testid="cart-free-shipping">✓ Free shipping applied</div>
              )}
              {quote && !quote.shipping_meta?.free_shipping_applied && quote.shipping_meta?.free_shipping_threshold && (() => {
                const gap = quote.shipping_meta.free_shipping_threshold - (quote.subtotal_after_discount ?? quote.subtotal);
                const step = nextOfferStep(items);
                return step > 0 && step >= gap ? (
                  <div className="mt-1 text-[11px] font-semibold text-primary" data-testid="cart-offer-free-shipping-nudge">Add one {bottleOffer().size_ml}ml offer bottle ({gbp(step)}) and unlock free {quote.shipping_meta.zone_name} shipping — saving {gbp(quote.shipping)}</div>
                ) : (
                  <div className="mt-1 text-[11px] text-muted-foreground">Add {gbp(gap)} more for free {quote.shipping_meta.zone_name} shipping</div>
                );
              })()}
            </>
          )}
          <div className="mt-3 flex justify-between border-t border-border pt-3 text-sm"><span className="font-semibold">Order total</span><span className="font-heading text-lg font-black text-primary mono" data-testid="cart-order-total">{gbp(quote ? quote.total : subtotal)}</span></div>
          <button data-testid="checkout-btn" onClick={() => nav("/checkout")} className="mt-6 w-full bg-foreground py-4 text-sm font-medium text-background transition-transform hover:-translate-y-0.5">
            Proceed to Checkout
          </button>
        </div>
      </div>
    </div>
  );
}
